import EventEmitter from "eventemitter3";
import { type RopCard } from "../models/dados-indicadores.model";

type MonitoringScope = {
  clientId: string | null;
  clusterId: string | null;
  usinaId: string | null;
};

type ErrorDetail = {
  logMessage: string;
  displayMessage: string;
};

type WSMessageEvent = {
  id?: string | null;
  event: string;
  data?: unknown;
};

// type WSMessage = {
//   type: string;
//   payload?: unknown;
// };

type MonitoringSessionConnectionEventTypes = {
  connected: () => void;
  reconnecting: (retryCount: number, reason?: string) => void;
  disconnected: (reason?: string) => void;
  error: (errorDetail: ErrorDetail) => void;
  ropCardUpdate: (ropCardUpdate: RopCard) => void;
};

export class MonitoringSessionConnection extends EventEmitter<MonitoringSessionConnectionEventTypes> {
  private static readonly _apiBaseUrl = import.meta.env.VITE_LINK_API;

  private readonly _token: string;
  private _monitoringScope: MonitoringScope | null;
  private _webSocket: WebSocket;
  private retries: number = 0;
  private isManuallyClosed: boolean = false;
  private reconnectTimeoutId: number | null = null;

  public constructor(token: string) {
    console.debug("Criando MonitoringSessionConnection");
    super();
    this._token = token;
    this._monitoringScope = null;
    this._webSocket = this.reconnect();
  }

  private static getMonitoringSessionUrl(token: string): URL {
    const wsUrl = new URL("/api/v1/ws/monitoringsession", this._apiBaseUrl);
    wsUrl.searchParams.set("token", token);
    return wsUrl;
  }

  private reconnect(): WebSocket {
    const url = MonitoringSessionConnection.getMonitoringSessionUrl(
      this._token
    );
    this._webSocket = new WebSocket(url.toString());
    this._webSocket.onopen = this.handleOpen.bind(this);
    this._webSocket.onerror = this.handleError.bind(this);
    this._webSocket.onclose = this.handleClose.bind(this);
    this._webSocket.onmessage = this.handleMessage.bind(this);
    this.emit("reconnecting", this.retries);
    return this._webSocket;
  }

  private handleOpen(_ev: Event) {
    console.debug("WebSocket Open", _ev);
    this.retries = 0;
    this.emit("connected");
    this.sendMonitoringScope();
  }

  private handleError(_ev: Event) {
    console.debug("WebSocket Error", _ev);
    // this.emit("error", "Erro na conexão WebSocket");
  }

  private handleClose(_ev: CloseEvent) {
    console.debug("WebSocket Close", _ev);
    this.emit("disconnected");

    if (this.isManuallyClosed) {
      return;
    }

    const delay = Math.min(1_000 * 2 ** this.retries, 30_000);
    console.debug(`[WS] Conexão perdida. Tentando reconectar em ${delay}ms...`);

    this.reconnectTimeoutId = window.setTimeout(() => {
      this.retries++;
      this._webSocket = this.reconnect();
    }, delay);
  }

  private handleMessage(ev: MessageEvent<unknown>) {
    // console.debug("WebSocket Message", ev);
    if (typeof ev.data !== "string") {
      console.warn("WebSocket Message com tipo não suportado", ev);
      return;
    }

    let wsevt: unknown;
    try {
      wsevt = JSON.parse(ev.data);
    } catch (error) {
      if (ev.data.slice(0, 10).toLowerCase().startsWith("erro")) {
        this.handleClientError({ event: "clienterror", data: ev.data });
        return;
      }

      console.warn(error, "Erro ao fazer parse da mensagem WebSocket", ev);
      return;
    }

    if (typeof wsevt !== "object" || !wsevt || !("event" in wsevt)) {
      console.warn("WebSocket Message sem campo 'event'", ev);
      return;
    }

    switch (wsevt.event) {
      case "ropCardUpdate":
        if (!("data" in wsevt)) {
          console.warn(
            "WebSocket Message ropCardUpdate com data inválido",
            wsevt
          );
          return;
        }

        this.handleRopCardUpdate({
          event: wsevt.event,
          data: wsevt.data,
        });
        break;
      default:
        console.warn("WebSocket Message com tipo desconhecido", wsevt);
        return;
    }
  }

  private handleRopCardUpdate(wsme: WSMessageEvent) {
    const ropCardUpdate: RopCard = wsme.data as RopCard;
    this.emit("ropCardUpdate", ropCardUpdate);
  }

  private handleClientError(wsme: WSMessageEvent) {
    const errorMessage: string = wsme.data as string;
    const wsErrorDetail = new WSErrorDetail(errorMessage);
    this.emit("error", wsErrorDetail);
  }

  public sendMessage(type: string, payload: unknown) {
    const message = { type, payload };
    this._webSocket.send(JSON.stringify(message));
  }

  private sendMonitoringScope() {
    if (!this._monitoringScope) {
      return;
    }

    this.sendMessage("SET_FILTER", this._monitoringScope);
  }

  public updateMonitoringScope(scope: MonitoringScope) {
    this._monitoringScope = scope;

    if (this._webSocket.readyState === WebSocket.OPEN) {
      this.sendMonitoringScope();
    }
  }

  private disconnect(reason?: string) {
    console.debug("Desconectando MonitoringSessionConnection", reason);
    this.emit("disconnected", reason);

    this.isManuallyClosed = true;
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (reason) {
      this._webSocket.close(1000, reason);
    } else {
      this._webSocket.close();
    }
  }

  public destroy() {
    console.debug("Destruindo MonitoringSessionConnection");
    this.disconnect("Cliente cancelou a conexão");
    this.removeAllListeners();
  }
}

class WSErrorDetail implements ErrorDetail {
  private static readonly _prefixsToRemove = Object.freeze([
    "Erro ao processar mensagem:",
  ]);

  public readonly logMessage: string;

  public constructor(errorMessage: string) {
    this.logMessage = errorMessage;
  }

  public get displayMessage(): string {
    for (const prefix of WSErrorDetail._prefixsToRemove) {
      if (this.logMessage.startsWith(prefix)) {
        return this.logMessage.slice(prefix.length).trim();
      }
    }
    return this.logMessage;
  }
}
